import React, { SetStateAction } from 'react';
import { DeleteOutlined } from '@ant-design/icons';
import uuid from 'short-uuid';
import {
	BunkerStemAccountTypes,
	BunkerStemAdditionalCostTypes,
	BunkerStemQuantityStatus,
	BunkerStemSuppliedAsPartOf,
	Currencies,
	FixtureTypes,
	FuelTypes,
} from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import { capitalize } from '@shared/utils/string';
import { getBunkerStemParcelDescription } from '@shared/utils/getBunkerStemParcelDescription';
import type {
	AdditionalCost,
	Parcel,
} from '@api/features/voyages/bunker-stems/createBunkerStem';
import AddButton from '../AddButton';
import Button from '../Button';
import { EditableColumns } from '../EditableTableRedux/EditableCellTableRedux';
import NumericInput from '../NumericInput';

const getAddButton = (
	onAdd: () => void,
	onDelete: (id: number | string | undefined) => void,
) => {
	return {
		title: (
			<AddButton
				onClick={onAdd}
			/>),
		dataIndex: 'addEntry',
		type: 'add',
		render: (row: Parcel) => (
			<Button
				onClick={() => onDelete(row.id)}
				type="text"
				confirmTitle="Are you sure you want to delete this row?"
				danger
				icon={(<DeleteOutlined />)}
			/>
		),
	};
};

type ParcelTableProps = {
	fixtureType: Values<typeof FixtureTypes>;
	paidBy: number | undefined;
	currency: Values<typeof Currencies>;
	setParcels: React.Dispatch<SetStateAction<Parcel[]>>;
	rebillToOptions: Array<{ value: number; label: string }>;
	paidByCounterparties: Array<{
		id: number;
		name: string;
		type: BunkerStemAccountTypes;
	}>;
	fuelGrades: Array<keyof typeof FuelTypes>;
};

export const getParcelTableColumns = ({
	fixtureType,
	paidBy,
	currency,
	setParcels,
	rebillToOptions,
	paidByCounterparties,
	fuelGrades,
}: ParcelTableProps) => {
	const onAdd = () => {
		setParcels((prev) => [
			...prev,
			{
				id: uuid.generate(),
				description: null,
				account: 'owner',
				fuelGrade: FuelTypes.VLSFO,
				quantityStatus: BunkerStemQuantityStatus.ESTIMATED,
				quantity: 0,
				minQuantity: 0,
				maxQuantity: null,
				basisPPT: 0,
				liftedPPT: 0,
				currency,
				subjectToCosts: true,
				suppliedAsPartOf: null,
				rebillTo: null,
			},
		]);
	};

	const onDelete = (
		id: number | string | undefined,
	) => setParcels((prev) => prev.filter((p) => p.id !== id));

	const hasTcIn = paidByCounterparties.find((b) => b.type === BunkerStemAccountTypes.TC_IN_OWNER) != null;
	const vcOutSuppliedAsPartOf: BunkerStemSuppliedAsPartOf[] = [];
	const tcOutSuppliedAsPartOf: BunkerStemSuppliedAsPartOf[] = [
		BunkerStemSuppliedAsPartOf.TC_OUT_DELIVERY,
		BunkerStemSuppliedAsPartOf.TC_OUT_REDELIVERY
	];
	const bbOutSuppliedAsPartOf: BunkerStemSuppliedAsPartOf[] = [
		BunkerStemSuppliedAsPartOf.BB_OUT_DELIVERY,
		BunkerStemSuppliedAsPartOf.BB_OUT_REDELIVERY
	];

	if (hasTcIn) {
		vcOutSuppliedAsPartOf.push(BunkerStemSuppliedAsPartOf.TC_IN_DELIVERY);
		tcOutSuppliedAsPartOf.push(BunkerStemSuppliedAsPartOf.TC_IN_DELIVERY);
		bbOutSuppliedAsPartOf.push(BunkerStemSuppliedAsPartOf.TC_IN_DELIVERY);
	}

	let relevantSuppliedAsPartOfOptions = tcOutSuppliedAsPartOf;

	if (fixtureType === FixtureTypes.SPOT) {
		relevantSuppliedAsPartOfOptions = vcOutSuppliedAsPartOf;
	}

	if (fixtureType === FixtureTypes.BB_OUT) {
		relevantSuppliedAsPartOfOptions = bbOutSuppliedAsPartOf;
	}

	const suppliedAsPartOf = relevantSuppliedAsPartOfOptions
		.map((value) => ({
			label: value,
			value: value,
		}));

	return [
		{
			title: 'Description',
			dataIndex: 'description',
			editable: false,
			width: 100,
			type: 'text',
			render: (row: Parcel) => {
				if (!row) {
					return '-';
				}

				const rebillType = paidByCounterparties.find((c) => c.id === row.rebillTo)?.type;
				const paidByType = paidByCounterparties.find((c) => c.id === paidBy)?.type;

				const str = getBunkerStemParcelDescription(
					fixtureType,
					paidByType ?? null,
					rebillType ?? null,
					row.suppliedAsPartOf,
				);

				return (
					<p style={{ padding: 0, margin: 0, fontSize: 12 }}>{str}</p>
				);
			},
		},
		{
			title: 'Fuel grade',
			dataIndex: 'fuelGrade',
			editable: true,
			width: 100,
			type: 'select',
			inputProps: {
				options: fuelGrades.map((key) => ({
					label: FuelTypes[key as keyof typeof FuelTypes],
					value: key,
				})),
			},
		},
		{
			title: 'Quantity status',
			dataIndex: 'quantityStatus',
			editable: true,
			type: 'select',
			width: 150,
			inputProps: {
				defaultValue: 'estimated',
				options: [{
					label: 'Est.',
					value: 'estimated',
				}, {
					label: 'Act.',
					value: 'actual'
				}],
			}
		},
		{
			title: 'Operational quantity',
			dataIndex: 'quantity',
			editable: true,
			type: 'number',
			width: 100,
			copyInputToColNames: ['minQuantity'],
			inputProps: {
				'data-testid': 'stem-quantity',
			},
			transformData: {
				in: Math.abs,
				out: Math.abs,
			},
		},
		{
			title: 'Min quantity',
			dataIndex: 'minQuantity',
			width: 100,
			editable: true,
			type: 'number',
			transformData: {
				in: Math.abs,
				out: Math.abs,
			},
		},
		{
			title: 'Max quantity',
			dataIndex: 'maxQuantity',
			width: 100,
			editable: true,
			type: 'number',
			transformData: {
				in: (v) => typeof v === 'number' ? Math.abs(v) : null,
				out: (v) => typeof v === 'number' ? Math.abs(v) : null,
			},
		},
		{
			title: 'Rebill to',
			dataIndex: 'rebillTo',
			editable: true,
			type: 'select',
			width: 200,
			inputProps: {
				options: rebillToOptions,
				allowClear: true,
			}
		},
		{
			title: 'Supplied as part of',
			dataIndex: 'suppliedAsPartOf',
			editable: true,
			type: 'select',
			width: 200,
			inputProps: {
				options: suppliedAsPartOf,
				allowClear: true,
			}
		},
		{
			title: 'Subject to costs',
			dataIndex: 'subjectToCosts',
			editable: true,
			type: 'checkbox',
		},
		{
			title: 'Basis price / MT',
			dataIndex: 'basisPPT',
			editable: true,
			type: 'currency',
			width: 150,
			inputProps: {
				currency,
				'data-testid': 'stem-price',
			},
		},
		{
			title: 'Lifted price / MT',
			dataIndex: 'liftedPPT',
			editable: false,
			type: 'currency',
			inputProps: {
				currency,
			},
		},
		getAddButton(
			onAdd,
			onDelete,
		),
	] as EditableColumns<Parcel>;
};

type AdditionalCostTableProps = {
	additionalCosts: Array<AdditionalCost>;
	setAdditionalCosts: React.Dispatch<SetStateAction<AdditionalCost[]>>;
	currency: Values<typeof Currencies>;
};

export const getAdditionalCostTableColumns = ({
	additionalCosts,
	setAdditionalCosts,
	currency,
}: AdditionalCostTableProps) => {
	const onAdd = () => {
		setAdditionalCosts((prev) => [
			...prev,
			{
				id: uuid.generate(),
				name: null,
				type: BunkerStemAdditionalCostTypes.LUMPSUM,
				amount: null,
			},
		]);
	};

	const onDelete = (id: number | string | undefined) => setAdditionalCosts((prev) => prev
		.filter((p) => p.id?.toString() !== id?.toString()));

	return [
		{
			title: 'Name',
			dataIndex: 'name',
			editable: true,
			type: 'text',
			inputProps: {
				placeholder: 'Name',
			},
		},
		{
			title: 'Type',
			dataIndex: 'type',
			editable: true,
			type: 'select',
			inputProps: {
				options: Object.values(BunkerStemAdditionalCostTypes).map((b) => ({
					label: capitalize(b),
					value: b,
				})),
			},
		},
		{
			title: 'Amount',
			dataIndex: 'amount',
			editable: true,
			type: 'number',
			inputProps: {
				placeholder: 'Amount',
			},
			render: (props: AdditionalCost) => {
				if (props == null) {
					return null;
				}

				const isPercentage = props.type === BunkerStemAdditionalCostTypes.PERCENTAGE;

				const row = additionalCosts.find((r) => r.id === props.id);

				if (row == null) {
					return null;
				}

				const updateValue = (value: number | null) => {
					setAdditionalCosts((prev) => prev.map((r) => {
						if (r.id === props.id) {
							return {
								...r,
								amount: value,
							};
						}

						return r;
					}));
				};

				return (
					<NumericInput
						value={row.amount}
						onChange={updateValue}
						bordered={false}
						max={isPercentage ? 100 : undefined}
						allowNegative={false}
						suffix={
							isPercentage ?
								'%' :
								currency
						}
					/>
				);
			},
		},
		getAddButton(
			onAdd,
			onDelete,
		),
	] as EditableColumns<AdditionalCost>;
};
